import type { ReactNode } from "react";
import type { Customer } from "@/api/types";
import { Button, type ButtonSize, type ButtonVariant } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PhoneFilledIcon } from "@/ui/components/voice-icons";
import { useVoiceCallingEnabled } from "./use-voice-calling";
import { useVoice } from "./voice-provider";

interface CallButtonProps {
  phoneNumber?: string | null;
  /** Display name stored on the call (skips the contact lookup). */
  name?: string;
  /** When available, the dialer's confirm stage shows their avatar. */
  customer?: Partial<Customer>;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  iconClassName?: string;
  children?: ReactNode;
}

/**
 * Opens the floating dialer on a confirmation stage for the given number
 * (an explicit confirm is required before dialing). Renders nothing when
 * web calling is unavailable (feature flag off, gym without a phone
 * number) or the contact has no phone number.
 */
export function CallButton({
  phoneNumber,
  name,
  customer,
  variant = "outline",
  size = "xs",
  className,
  iconClassName,
  children,
}: CallButtonProps) {
  const enabled = useVoiceCallingEnabled();
  const { status, requestCall } = useVoice();

  if (!enabled || !phoneNumber) {
    return null;
  }

  const label = name ? `Call ${name}` : "Call";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* Always labelled: callers may pass children that are hidden at
              narrow container widths (icon-only collapse), so visible text
              alone can't be relied on for the accessible name. */}
          <Button
            variant={variant}
            size={size}
            className={cn("gap-1.5", className)}
            disabled={status !== "idle"}
            onClick={() => requestCall({ to: phoneNumber, name, customer })}
            aria-label={label}
          >
            <PhoneFilledIcon className={cn("size-3.5 shrink-0", iconClassName)} />
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
